import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import { Search, Printer, Edit2, X } from 'lucide-react';
import './OngoingOrders.css';

const FrontOngoingOrders = ({ baseUrl, getHeaders, onPayOrder, onEditOrder }) => {
  const { user } = useContext(UserContext);
  const [orders, setOrders]         = useState([]);
  const [activeTab, setActiveTab]   = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [printOrder, setPrintOrder]   = useState(null);
  const [showPrintPopup, setShowPrintPopup] = useState(false);

  // ── Fetch active orders ────────────────────────────────────────────────
  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/activeorders`, {
        headers: getHeaders(),
      });
      const data = Array.isArray(response.data) ? response.data : [];
      const now  = new Date();

      // Keep only incomplete orders and skip stale data > 48 h
      const ongoing = data.filter(o => {
        if (o.status === 'assigned') return false;
        const diffHours = (now - new Date(o.timestamp)) / (1000 * 60 * 60);
        if (diffHours > 48) return false;
        const isCompleted =
          o.paid &&
          o.cartItems &&
          o.cartItems.every(item => (item.servedQuantity || 0) >= (item.quantity || 1));
        return !isCompleted;
      });

      // Newest first
      ongoing.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setOrders(ongoing);
    } catch (error) {
      console.error('Error fetching ongoing orders:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────
  const formatPrice = amount =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

  const calculateTotal = cartItems => {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((total, item) => {
      const excl = Number(item.exclTotal)  || Number(item.excl_amount) || 0;
      const tax  = Number(item.taxTotal)   || Number(item.tax_amount)  || 0;
      return total + excl + tax;
    }, 0);
  };

  const getElapsedTime = timestamp => {
    const diffMins = Math.floor((new Date() - new Date(timestamp)) / 60000);
    if (diffMins < 1) return 'Just now';
    return `${diffMins} mins`;
  };

  // Returns true when every item in the order has been fully served
  const isAllServed = order => {
    if (!order.cartItems || order.cartItems.length === 0) return false;
    return order.cartItems.every(
      item => (item.servedQuantity || 0) >= (item.quantity || 1)
    );
  };

  const handleDelete = async orderId => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await axios.delete(`${baseUrl}/api/activeorders/${orderId}`, {
        headers: getHeaders(),
      });
      fetchOrders();
    } catch (error) {
      console.error('Failed to delete order', error);
      alert('Failed to delete order');
    }
  };

  // ── Order-type helpers ────────────────────────────────────────────────
  const inferOrderType = order => {
    if (order.tableNumber)    return 'Dine In';
    if (order.deliveryAddress) return 'Online Delivery';
    return 'Takeaway';
  };

  const normalizeOrderType = type => {
    if (!type) return 'Takeaway';
    const t = type.toLowerCase().replace(/[-\s]/g, '');
    if (t === 'takeaway' || t === 'takeway' || t === 'togo') return 'Takeaway';
    if (t === 'dinein'   || t === 'dine')                    return 'Dine In';
    if (t === 'online'   || t === 'onlinedelivery' || t === 'delivery') return 'Online Delivery';
    return type;
  };

  // ── Filtering ─────────────────────────────────────────────────────────
  const filteredOrders = orders.filter(order => {
    const type = normalizeOrderType(order.orderType || inferOrderType(order));

    if (activeTab === 'Dine in'  && type !== 'Dine In')        return false;
    if (activeTab === 'To go'    && type !== 'Takeaway')        return false;
    if (activeTab === 'Delivery' && type !== 'Online Delivery') return false;

    if (searchQuery) {
      const q      = searchQuery.toLowerCase().trim();
      const qClean = q.startsWith('#') ? q.slice(1) : q;
      if (
        !(order.orderNo      || '').toLowerCase().includes(qClean) &&
        !(order.orderId      || '').toLowerCase().includes(qClean) &&
        !(order.customerName || '').toLowerCase().includes(q) &&
        !(order.tableNumber  || '').toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="ongoing-orders-container">

      {/* Header */}
      <div className="ongoing-header">
        <h1>Ongoing Orders</h1>
        <div className="ongoing-search-bar">
          <Search size={18} className="ongoing-search-icon" />
          <input
            type="text"
            placeholder="Search by token, table, or name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#94a3b8', padding: '0 4px',
                display: 'flex', alignItems: 'center',
              }}
              title="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="ongoing-tabs">
        {['All', 'Dine in', 'To go', 'Delivery'].map(tab => (
          <button
            key={tab}
            className={`ongoing-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Card Grid */}
      <div className="ongoing-grid">
        {filteredOrders.length === 0 ? (
          <div className="ongoing-empty">
            <p>No ongoing orders found.</p>
          </div>
        ) : (
          filteredOrders.map(order => {
            const type = normalizeOrderType(order.orderType || inferOrderType(order));
            let typeClass   = 'type-dinein';
            let displayType = 'Dine in';
            if (type === 'Takeaway') {
              typeClass   = 'type-togo';
              displayType = 'To go';
            } else if (type === 'Online Delivery') {
              typeClass   = 'type-delivery';
              displayType = 'Delivery';
            }

            const paid   = order.paid;
            const served = isAllServed(order);

            return (
              <div key={order.orderId} className={`ongoing-card ${typeClass}`}>

                {/* ── Card body ── */}
                <div className="ongoing-card-content">

                  {/* Row 1: Table / Token */}
                  <div className="ongoing-card-row">
                    <span className="ongoing-table">
                      {order.tableNumber
                        ? `Table No. ${order.tableNumber}`
                        : 'Table No. N/A'}
                    </span>
                    <span className="ongoing-order-id">
                      #{order.orderNo || order.orderId.substring(0, 8)}
                    </span>
                  </div>

                  {/* Row 2: Name / Total */}
                  <div className="ongoing-card-row">
                    <span className="ongoing-name">
                      Name: {order.customerName || 'N/A'}
                    </span>
                    <span className="ongoing-total">
                      {formatPrice(calculateTotal(order.cartItems))}
                    </span>
                  </div>

                  {/* Row 3: Action buttons */}
                  <div className="ongoing-actions">
                    {!paid && (
                      <button
                        className="btn-pay"
                        onClick={() => onPayOrder(order)}
                      >
                        Pay
                      </button>
                    )}
                    {!paid && (
                      <button
                        className="btn-paylater"
                        onClick={() => alert('Order marked for pay later')}
                      >
                        Pay later
                      </button>
                    )}
                    <button
                      className="btn-icon"
                      onClick={() => handleDelete(order.orderId)}
                      title="Delete order"
                    >
                      <X size={15} />
                    </button>
                    <button 
                      className="btn-icon" 
                      title="Print"
                      onClick={() => {
                        setPrintOrder(order);
                        setShowPrintPopup(true);
                      }}
                    >
                      <Printer size={15} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => onEditOrder(order)}
                      title="Edit order"
                    >
                      <Edit2 size={15} />
                    </button>
                  </div>

                  {/* Row 4: Status badges */}
                  <div style={{
                    marginTop: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                  }}>
                    {/* Payment badge */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '3px 10px', borderRadius: '20px',
                      fontSize: '0.78rem', fontWeight: '700',
                      background: paid ? '#dcfce7' : '#fee2e2',
                      color:      paid ? '#15803d' : '#b91c1c',
                      border:    `1px solid ${paid ? '#86efac' : '#fca5a5'}`,
                    }}>
                      <span style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        display: 'inline-block',
                        background: paid ? '#22c55e' : '#ef4444',
                      }} />
                      {paid ? 'Paid' : 'Not Paid'}
                    </span>

                    {/* Served badge */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '3px 10px', borderRadius: '20px',
                      fontSize: '0.78rem', fontWeight: '700',
                      background: served ? '#dbeafe' : '#fff7ed',
                      color:      served ? '#1d4ed8' : '#c2410c',
                      border:    `1px solid ${served ? '#93c5fd' : '#fdba74'}`,
                    }}>
                      <span style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        display: 'inline-block',
                        background: served ? '#3b82f6' : '#f97316',
                      }} />
                      {served ? 'Served' : 'Not Served'}
                    </span>
                  </div>

                </div>{/* end card-content */}

                {/* ── Card footer ── */}
                <div className="ongoing-card-footer">
                  <span className="ongoing-footer-type">{displayType}</span>
                  <span className="ongoing-footer-time">
                    ⏱ {getElapsedTime(order.timestamp)}
                  </span>
                </div>

              </div>
            );
          })
        )}
      </div>

      {/* ── Print Modal Popup ── */}
      {showPrintPopup && printOrder && (
        <div className="ongoing-print-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="ongoing-print-modal" style={{ background: '#fff', padding: '20px', borderRadius: '12px', width: '90%', maxWidth: '400px', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            <button 
              onClick={() => setShowPrintPopup(false)} 
              style={{ position: 'absolute', top: '15px', right: '15px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#000' }}
            >
              <X size={20} />
            </button>
            
            <div id="print-receipt-content" style={{ padding: '10px', color: '#000', fontFamily: 'monospace' }}>
              <h3 style={{ textAlign: 'center', margin: '0 0 10px 0' }}>Order Receipt</h3>
              <p style={{ margin: '5px 0' }}><strong>Order No:</strong> #{printOrder.orderNo || printOrder.orderId.substring(0, 8)}</p>
              <p style={{ margin: '5px 0' }}><strong>Customer:</strong> {printOrder.customerName || 'N/A'}</p>
              <p style={{ margin: '5px 0' }}><strong>Type:</strong> {printOrder.orderType || 'N/A'}</p>
              {printOrder.tableNumber && <p style={{ margin: '5px 0' }}><strong>Table:</strong> {printOrder.tableNumber}</p>}
              <p style={{ margin: '5px 0', borderBottom: '1px dashed #000', paddingBottom: '10px' }}><strong>Date:</strong> {new Date(printOrder.timestamp).toLocaleString()}</p>
              
              <table style={{ width: '100%', marginTop: '10px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #000' }}>
                    <th style={{ textAlign: 'left', padding: '5px 0' }}>Item</th>
                    <th style={{ textAlign: 'center', padding: '5px 0' }}>Qty</th>
                    <th style={{ textAlign: 'right', padding: '5px 0' }}>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {(printOrder.cartItems || []).map((item, idx) => {
                    const excl = Number(item.exclTotal) || Number(item.excl_amount) || 0;
                    const tax  = Number(item.taxTotal)  || Number(item.tax_amount)  || 0;
                    const rowTotal = excl + tax;
                    const addonPrices = item.addonPrices || {};

                    // Build display name with size in brackets like cart: "Hotdog Burger (M)"
                    const displayName = item.selectedSize
                      ? `${item.item_name || item.name} (${item.selectedSize})`
                      : (item.item_name || item.name);

                    // Calculate totals for addons, toppings, and variants
                    const addonTotal = Object.entries(item.addonQuantities || {}).reduce((sum, [name, qty]) => {
                      return sum + (Number(addonPrices[name]) || 0) * Number(qty);
                    }, 0);
                    
                    const toppingTotal = Object.values(item.itemToppings || {}).reduce((sum, t) => {
                      return sum + (Number(t.price) || 0) * (Number(t.qty) || 1);
                    }, 0);

                    const spicyPrice = item.isSpicy ? (Number(item.spicyPrice) || 0) : 0;
                    const icePrice   = (item.icePreference && item.icePreference !== 'without_ice') ? (Number(item.icePrice) || 0) : 0;
                    
                    // Subtract all extras to get the main item's base price
                    const baseRowPrice = rowTotal - addonTotal - toppingTotal - spicyPrice - icePrice;

                    return (
                      <React.Fragment key={idx}>
                        {/* Main item row */}
                        <tr>
                          <td style={{ padding: '4px 0' }}>{displayName}</td>
                          <td style={{ textAlign: 'center', padding: '4px 0' }}>{item.quantity}</td>
                          <td style={{ textAlign: 'right', padding: '4px 0' }}>₹{baseRowPrice.toFixed(2)}</td>
                        </tr>
                        {/* Spicy row */}
                        {item.isSpicy && (
                          <tr>
                            <td style={{ padding: '2px 0 2px 12px', color: '#555', fontSize: '0.9em' }}>+ Spicy</td>
                            <td style={{ textAlign: 'center', padding: '2px 0', color: '#555', fontSize: '0.9em' }}>1</td>
                            <td style={{ textAlign: 'right', padding: '2px 0', color: '#555', fontSize: '0.9em' }}>
                              {spicyPrice > 0 ? `₹${spicyPrice.toFixed(2)}` : '-'}
                            </td>
                          </tr>
                        )}
                        {/* Ice preference row */}
                        {item.icePreference && item.icePreference !== 'without_ice' && (
                          <tr>
                            <td style={{ padding: '2px 0 2px 12px', color: '#555', fontSize: '0.9em' }}>+ Ice ({item.icePreference})</td>
                            <td style={{ textAlign: 'center', padding: '2px 0', color: '#555', fontSize: '0.9em' }}>1</td>
                            <td style={{ textAlign: 'right', padding: '2px 0', color: '#555', fontSize: '0.9em' }}>
                              {icePrice > 0 ? `₹${icePrice.toFixed(2)}` : '-'}
                            </td>
                          </tr>
                        )}
                        {/* Addon rows (if any) */}
                        {Object.entries(item.addonQuantities || {}).map(([addonName, qty], i) => {
                          const unitPrice = Number(addonPrices[addonName]) || 0;
                          const total = unitPrice * Number(qty);
                          return (
                            <tr key={`addon-${i}`}>
                              <td style={{ padding: '2px 0 2px 12px', color: '#555', fontSize: '0.9em' }}>+ {addonName}</td>
                              <td style={{ textAlign: 'center', padding: '2px 0', color: '#555', fontSize: '0.9em' }}>{qty}</td>
                              <td style={{ textAlign: 'right', padding: '2px 0', color: '#555', fontSize: '0.9em' }}>
                                {total > 0 ? `₹${total.toFixed(2)}` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                        {/* Item Toppings rows */}
                        {Object.values(item.itemToppings || {}).map((t, i) => {
                          const total = (Number(t.price) || 0) * (Number(t.qty) || 1);
                          return (
                            <tr key={`topping-${i}`}>
                              <td style={{ padding: '2px 0 2px 12px', color: '#555', fontSize: '0.9em' }}>+ {t.name} (Topping)</td>
                              <td style={{ textAlign: 'center', padding: '2px 0', color: '#555', fontSize: '0.9em' }}>{t.qty || 1}</td>
                              <td style={{ textAlign: 'right', padding: '2px 0', color: '#555', fontSize: '0.9em' }}>
                                {total > 0 ? `₹${total.toFixed(2)}` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                        {/* Separator between items */}
                        <tr><td colSpan="3" style={{ borderBottom: '1px dashed #ccc', padding: '2px 0' }} /></tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ borderTop: '1px dashed #000', marginTop: '10px', paddingTop: '10px', textAlign: 'right', fontSize: '1.2em', fontWeight: 'bold' }}>
                Total: {formatPrice(calculateTotal(printOrder.cartItems))}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <button 
                onClick={() => {
                  const printContents = document.getElementById('print-receipt-content').innerHTML;
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write('<html><head><title>Print Receipt</title>');
                  printWindow.document.write('</head><body style="font-family: monospace; padding: 20px;">');
                  printWindow.document.write(printContents);
                  printWindow.document.write('</body></html>');
                  printWindow.document.close();
                  printWindow.focus();
                  setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                  }, 250);
                }}
                style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}
              >
                <Printer size={18} /> Print Now
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FrontOngoingOrders;
